import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorScreen } from '@/components/ErrorScreen'
import { readingApi, type RecommendationMode } from '@/api/reading'

function parseMode(value: string | null): RecommendationMode {
  if (value === 'keep_learning' || value === 'surprise') {
    return value
  }
  return 'default'
}

export function RecommendedReadingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const mode = parseMode(searchParams.get('mode'))

  const recommendationQuery = useQuery({
    queryKey: ['readingRecommended', mode],
    queryFn: () => readingApi.getRecommended(mode),
    retry: false,
  })

  useEffect(() => {
    if (recommendationQuery.data) {
      const keepLearning = mode === 'keep_learning' ? '1' : undefined
      const query = keepLearning ? `?keepLearning=${keepLearning}` : ''
      navigate(`/reading/${recommendationQuery.data.article.id}${query}`, { replace: true })
    }
  }, [recommendationQuery.data, mode, navigate])

  if (recommendationQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="recommended-reading-loading">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (recommendationQuery.isError) {
    return (
      <ErrorScreen
        variant="network"
        title="No recommendation available"
        description="Try browsing the catalog or adjust your target level in Settings."
        onRetry={() => recommendationQuery.refetch()}
      />
    )
  }

  return (
    <div className="flex h-64 items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}
